import Link from "next/link"
import Image from "next/image"
import { CanonicalImage } from "@/components/ui/canonical-image"
import { Snowflake, Sun } from "lucide-react"
import { BlogShareButtons } from "@/components/ui/blog-share-buttons"
import { MobileShareButton, HERO_CARD_SHARE_CLASS } from "@/components/ui/mobile-share-button"
import { formatBlogDate } from "@/lib/blog-post-page-utils"
import type { GrassGuide } from "@/data/guide-content"
import type { GuideMetadata } from "@/data/guide-slugs"
import type { Author } from "@/lib/types/author"
import { imageCreditTitle, parseImageCreditText } from "@/lib/image-credits"

interface GuidePhotoHeroProps {
  guide: GrassGuide
  meta: GuideMetadata
  author: Author
  readingMinutes?: number
  /** Admin-managed hero image URL (Directus). Null/undefined = brand gradient. */
  heroImageUrl?: string | null
  /** Photo credit; shown only as the image's title tooltip, never as a caption. */
  imageCredit?: string | null
}

// Layered dark gradient over the cover photo (or brand-gradient fallback).
// Keeps the white headline + glass chrome legible no matter how bright the
// underlying image is. Mirrors the blog post hero (Wild Violet v2) exactly so
// guides and blog posts share one editorial photo-hero language.
const HERO_SCRIM =
  "linear-gradient(180deg, rgba(8,24,15,0.55) 0%, rgba(8,24,15,0.10) 26%, rgba(8,24,15,0.34) 60%, rgba(8,24,15,0.86) 100%)"

/**
 * Full-bleed editorial photo hero for guide pages. The cover image fills the
 * section behind a dark scrim; breadcrumb + share float top, and the
 * category pill + serif title + dark-glass author/meta bar sit at the bottom.
 *
 * The visual breadcrumb is decorative: the BreadcrumbList JSON-LD is emitted
 * by the page (`app/guides/[slug]/page.tsx`), so light-on-photo markup loses
 * no schema. No grass photography exists yet, so guides render on the brand
 * gradient until `guide.heroImage` is populated.
 */
export function GuidePhotoHero({
  guide,
  meta,
  author,
  readingMinutes,
  heroImageUrl,
  imageCredit,
}: GuidePhotoHeroProps) {
  const isCool = meta.season === "cool"
  const hasImage = Boolean(heroImageUrl)
  const credit = parseImageCreditText(imageCredit)

  return (
    <header className="hero-bleed-under-header relative flex min-h-[44svh] flex-col overflow-hidden md:min-h-[50svh]">
      {/* Cover photo (or brand-gradient fallback) */}
      <div className="absolute inset-0 z-0">
        {hasImage ? (
          <CanonicalImage
            src={heroImageUrl as string}
            alt={`${meta.displayName} lawn`}
            title={credit ? imageCreditTitle(credit) : undefined}
            fill
            priority
            fetchPriority="high"
            quality={82}
            className="object-cover"
            sizes="100vw"
          />
        ) : (
          <div className="h-full w-full bg-linear-to-br from-green-700 via-emerald-700 to-green-900" />
        )}
        <div className="absolute inset-0" style={{ background: HERO_SCRIM }} />
      </div>

      {/* Top row: breadcrumb + share, floating over the photo. hero-clear-header
          replaces the old pt-5 — the photo now runs under the header pill, so this
          row has to clear it or its contents render behind the pill (z-50 vs z-10).
          On mobile the row is intentionally empty (breadcrumb is sm+, share moved
          into the meta card); its padding is what keeps the title clear of the pill. */}
      <div className="hero-clear-header relative z-10 mx-auto flex w-full max-w-7xl items-center justify-between gap-3 px-4 md:px-6 lg:px-8">
        <nav
          aria-label="Breadcrumb"
          className="hidden min-w-0 items-center gap-1.5 text-[12.5px] font-medium sm:flex"
        >
          <Link href="/" className="text-white/80 transition-colors hover:text-white">
            Home
          </Link>
          <span className="text-white/40">/</span>
          <Link href="/guides" className="text-white/80 transition-colors hover:text-white">
            Guides
          </Link>
          <span className="text-white/40">/</span>
          <span className="truncate text-white/90">{meta.displayName}</span>
        </nav>
        <div className="ml-auto hidden md:block">
          <BlogShareButtons
            title={guide.title}
            description={guide.heroSubtitle}
            variant="glass"
          />
        </div>
      </div>

      {/* Bottom body: category pill, title, dark-glass meta bar */}
      <div className="relative z-10 mx-auto mt-auto w-full max-w-7xl px-4 pb-8 md:px-6 md:pb-12 lg:px-8">
        <div className="mb-4 flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/30 bg-white/[0.18] px-3.5 py-2 text-[13px] font-semibold text-white backdrop-blur-md">
            {isCool ? (
              <Snowflake className="h-3.5 w-3.5" aria-hidden="true" />
            ) : (
              <Sun className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            {isCool ? "Cool Season Grass" : "Warm Season Grass"}
          </span>
        </div>

        <h1
          id="guide-title"
          className="font-editorial mb-6 max-w-[20ch] font-semibold leading-[1.05] tracking-[-0.015em] text-white text-[clamp(2.1rem,1rem_+_4.4vw,4.4rem)] [text-shadow:0_2px_30px_rgba(0,0,0,0.35)]"
        >
          {guide.title}
        </h1>

        <div
          className="relative inline-flex max-w-full flex-wrap items-center gap-x-4 gap-y-3 rounded-[18px] border border-white/20 bg-[rgba(10,28,18,0.34)] px-4 py-3 backdrop-blur-xl [box-shadow:inset_0_1px_0_rgba(255,255,255,0.30),0_20px_48px_-18px_rgba(0,0,0,0.5)]"
          role="contentinfo"
          aria-label="Guide metadata"
        >
          <Link
            href={`/blog/author/${author.slug}`}
            prefetch={false}
            className="group flex items-center gap-2.5"
          >
            <span className="relative h-10 w-10 flex-shrink-0 overflow-hidden rounded-full ring-[1.5px] ring-white/60">
              <Image
                src={author.headshot.url}
                alt={author.headshot.alt}
                fill
                className="object-cover"
                sizes="40px"
              />
            </span>
            <span className="flex flex-col">
              <span className="text-sm font-bold leading-tight text-white transition-colors group-hover:text-green-200">
                {author.name}
              </span>
              <span className="text-xs text-white/70">{author.shortCredential}</span>
            </span>
          </Link>
          <span className="hidden h-7 w-px bg-white/25 sm:block" aria-hidden="true" />
          <div className="flex items-center gap-2.5 text-[12.5px] text-white/80">
            <span className="text-[11px] uppercase tracking-[0.06em] text-white/60">
              Updated
            </span>
            <time dateTime={guide.lastUpdated}>{formatBlogDate(guide.lastUpdated)}</time>
            {readingMinutes ? (
              <>
                <span className="text-white/50" aria-hidden="true">
                  &bull;
                </span>
                <span aria-label={`${readingMinutes} minute read`}>
                  {readingMinutes} min read
                </span>
              </>
            ) : null}
          </div>
          <MobileShareButton
            title={guide.title}
            description={guide.heroSubtitle}
            articleSlug={guide.slug}
            className={HERO_CARD_SHARE_CLASS}
          />
        </div>
      </div>
    </header>
  )
}
